import React, { Fragment, useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Input,
    Spinner,
    Collapse,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Table,
    FormGroup,
    Label,
    Nav,
    NavItem,
    NavLink,
} from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import CardHeaderSpan from '../../../Utils/CommonComponents/CardHeaderSpan';
import { getLabel } from '../../../sharedFunctions/CommonLable';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import { toast } from 'react-toastify';
import moment from 'moment';
import Loader from '../../../Layout/Loader';
import { validateGeofencingForDCR } from '../../../sharedFunctions/GeofencingHelper';
import { getSystemConfig } from '../../../sharedFunctions/SystemConfig';

import { useDispatch, useSelector } from 'react-redux';
import { ensureDexieReady, getDataFromDexie } from '../../../db/DexieServises';
import { RootState } from '../../../redux-toolkit/store';
import { setSelectedDate as setReduxSelectedDate } from '../../../redux-toolkit/positionSlice';

interface MeFWContainerProps {
    initialCategory?: 'Doctor' | 'Pharmacy' | 'Stockist' | 'All';
    onTabChange?: (tab: 'JW' | 'Doctor' | 'Pharmacy' | 'Stockist' | 'NCA') => void;
    onRefreshCounts?: () => void;
    agendaCounts?: {
        jw: number;
        doctorReported: number;
        doctorTotal: number;
        pharmacyReported: number;
        pharmacyTotal: number;
        stockistReported: number;
        stockistTotal: number;
        nca: number;
    };
}

export default function MeFWContainer({ initialCategory, onTabChange, onRefreshCounts, agendaCounts }: MeFWContainerProps = {}) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const reduxSelectedDate = useSelector((state: RootState) => state.position.selectedDate);
    const initialDate = location.state?.date || reduxSelectedDate || localStorage.getItem("selectedDate") || moment().format('YYYY-MM-DD');
    const [selectedDate, setSelectedDate] = useState<string>(initialDate);

    const [fwList, setFwList] = useState<any[]>([]);
    const [activeCategory, setActiveCategory] = useState<'Doctor' | 'Pharmacy' | 'Stockist' | 'All'>(
        initialCategory || location.state?.filter || 'Doctor'
    );
    const [loadingFwData, setLoadingFwData] = useState<boolean>(false);
    const [submittingFwData, setSubmittingFwData] = useState<boolean>(false);

    // Bridge API lists (getBrands, getEdPresentation, getSgpiBalance) with pagination
    const [brandsList, setBrandsList] = useState<any[]>([]);
    const [brandsPage, setBrandsPage] = useState<number>(1);
    const [brandsTotalPages, setBrandsTotalPages] = useState<number>(1);
    const [loadingBrands, setLoadingBrands] = useState<boolean>(false);

    const [edPresentationList, setEdPresentationList] = useState<any[]>([]);
    const [edPage, setEdPage] = useState<number>(1);
    const [edTotalPages, setEdTotalPages] = useState<number>(1);
    const [loadingEd, setLoadingEd] = useState<boolean>(false);

    const [sgpiBalanceList, setSgpiBalanceList] = useState<any[]>([]);
    const [sgpiPage, setSgpiPage] = useState<number>(1);
    const [sgpiTotalPages, setSgpiTotalPages] = useState<number>(1);
    const [loadingSgpi, setLoadingSgpi] = useState<boolean>(false);

    // Profile Reporting list from API.getProfile (/api/users/me)
    const [profileReportingList, setProfileReportingList] = useState<any[]>([]);
    const [loadingReporting, setLoadingReporting] = useState<boolean>(false);
    const [selectedReportingManagers, setSelectedReportingManagers] = useState<Record<number, (string | number)[]>>({});

    const [loadingBridgeData, setLoadingBridgeData] = useState<boolean>(false);

    // Multiselect state tracking per card index
    const [selectedBrands, setSelectedBrands] = useState<Record<number, (string | number)[]>>({});
    const [selectedDetailingBrands, setSelectedDetailingBrands] = useState<Record<number, (string | number)[]>>({});
    const [selectedEdPresentations, setSelectedEdPresentations] = useState<Record<number, (string | number)[]>>({});
    const [selectedSgpiBalances, setSelectedSgpiBalances] = useState<Record<number, (string | number)[]>>({});
    const [selectedSgpiQuantities, setSelectedSgpiQuantities] = useState<Record<number, Record<string | number, number>>>({});
    const [submittingFwIndex, setSubmittingFwIndex] = useState<number | null>(null);

    const toggleReportingManagerSelection = (cardIndex: number, mgrId: string | number) => {
        setSelectedReportingManagers(prev => {
            const currentList = prev[cardIndex] || [];
            const isAlreadySelected = currentList.includes(mgrId);
            const newList = isAlreadySelected
                ? currentList.filter(id => id !== mgrId)
                : [...currentList, mgrId];
            return { ...prev, [cardIndex]: newList };
        });
    };

    // Unplanned Call Modal State & Pagination
    const [unplannedModalOpen, setUnplannedModalOpen] = useState<boolean>(false);
    const [loadingOutlets, setLoadingOutlets] = useState<boolean>(false);
    const [submittingUnplannedCall, setSubmittingUnplannedCall] = useState<boolean>(false);

    const [outletsList, setOutletsList] = useState<any[]>([]);
    const [outletsPage, setOutletsPage] = useState<number>(1);
    const [outletsTotalPages, setOutletsTotalPages] = useState<number>(1);
    const [outletSearchText, setOutletSearchText] = useState<string>("");
    const [selectedOutletIds, setSelectedOutletIds] = useState<number[]>([]);

    // Fetch Outlets via /api/fetchOutlets
    const fetchOutletsList = async (
        page: number = 1,
        beatIdsStr?: string,
        territoryIdsStr?: string,
        outlettypeIdsStr?: string
    ) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingOutlets(true);
        try {
            const url = `${serverUrl}${API.fetchOutlets}`;
            const bVal = beatIdsStr !== undefined ? beatIdsStr : beatsCommaStr;
            const tVal = territoryIdsStr !== undefined ? territoryIdsStr : territoriesCommaStr;
            const oVal = outlettypeIdsStr !== undefined ? outlettypeIdsStr : outletTypesCommaStr;

            const params: Record<string, any> = { page, limit: 50 };
            if (bVal && bVal.trim()) params.beat_id = bVal.trim();
            if (tVal && tVal.trim()) params.territory_id = tVal.trim();
            if (oVal && oVal.trim()) params.outlettype_id = oVal.trim();

            console.log("Fetching outlets with API filter params:", params);
            const res = await getData(url, true, params);
            const { items, page: currPage, totalPages } = parseBridgeResponse(res);
            setOutletsList(items);
            setOutletsPage(currPage);
            setOutletsTotalPages(totalPages);
        } catch (err) {
            console.error("Error fetching outlets list:", err);
            setOutletsList([]);
        } finally {
            setLoadingOutlets(false);
        }
    };

    // Filter Modal & Selection State for Outlets
    const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);
    const [activeFilterTab, setActiveFilterTab] = useState<'type' | 'beats' | 'territories'>('type');
    const [loadingFilterData, setLoadingFilterData] = useState<boolean>(false);

    const [filterOutletTypes, setFilterOutletTypes] = useState<any[]>([]);
    const [filterBeats, setFilterBeats] = useState<any[]>([]);
    const [filterTerritories, setFilterTerritories] = useState<any[]>([]);

    const [selectedOutletTypeIds, setSelectedOutletTypeIds] = useState<(string | number)[]>([]);
    const [selectedBeatIds, setSelectedBeatIds] = useState<(string | number)[]>([]);
    const [selectedTerritoryIds, setSelectedTerritoryIds] = useState<(string | number)[]>([]);

    // Comma-separated ID strings stored in state
    const [outletTypesCommaStr, setOutletTypesCommaStr] = useState<string>("");
    const [beatsCommaStr, setBeatsCommaStr] = useState<string>("");
    const [territoriesCommaStr, setTerritoriesCommaStr] = useState<string>("");

    const fetchBeatsForTerritory = async (terrId?: string | number) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        try {
            const params = terrId ? { territory_id: terrId } : {};
            const resBeats = await getData(`${serverUrl}${API.getBeats}`, true, params);
            const rawBeats = resBeats?.data?.data || resBeats?.data || [];
            setFilterBeats(Array.isArray(rawBeats) ? rawBeats : []);
        } catch (err) {
            console.error("Error fetching beats:", err);
            setFilterBeats([]);
        }
    };

    const fetchFilterData = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingFilterData(true);
        try {
            // 1. Outlet Types: API.getOutletTypes
            const resTypes = await getData(`${serverUrl}${API.getOutletTypes}`, true);
            const rawTypes = resTypes?.data?.data || resTypes?.data || [];
            setFilterOutletTypes(Array.isArray(rawTypes) ? rawTypes : []);

            // 2. Territories: API.getTerritoriesArray (Dictionary mapping ID -> Name string)
            const resTerr = await getData(`${serverUrl}${API.getTerritoriesArray}`, true);
            const rawTerr = resTerr?.data?.data || resTerr?.data || {};
            let terrList: any[] = [];
            if (Array.isArray(rawTerr)) {
                terrList = rawTerr;
            } else if (typeof rawTerr === 'object' && rawTerr !== null) {
                terrList = Object.entries(rawTerr).map(([id, name]) => ({
                    id,
                    name,
                    TerritoryId: id,
                    TerritoryName: name
                }));
            }
            setFilterTerritories(terrList);

            // 3. Beats: API.getBeats (use selected territories or first territory if available)
            const terrIdParam = selectedTerritoryIds.length > 0
                ? selectedTerritoryIds.join(",")
                : (terrList.length > 0 ? terrList[0].id : undefined);
            await fetchBeatsForTerritory(terrIdParam);
        } catch (err) {
            console.error("Error fetching filter data:", err);
        } finally {
            setLoadingFilterData(false);
        }
    };

    const toggleOutletTypeSelection = (id: string | number) => {
        setSelectedOutletTypeIds(prev =>
            prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
        );
    };

    const toggleBeatSelection = (id: string | number) => {
        setSelectedBeatIds(prev =>
            prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
        );
    };

    const toggleTerritorySelection = (id: string | number) => {
        setSelectedTerritoryIds(prev => {
            const updated = prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id];
            const commaTerrs = updated.join(",");
            fetchBeatsForTerritory(commaTerrs);
            return updated;
        });
    };

    const handleApplyFilter = () => {
        const typeIdsStr = selectedOutletTypeIds.join(",");
        const beatIdsStr = selectedBeatIds.join(",");
        const terrIdsStr = selectedTerritoryIds.join(",");

        setOutletTypesCommaStr(typeIdsStr);
        setBeatsCommaStr(beatIdsStr);
        setTerritoriesCommaStr(terrIdsStr);

        console.log("================ FILTER APPLIED ================");
        console.log("outlettype_id (comma-separated IDs):", typeIdsStr);
        console.log("beat_id (comma-separated IDs):", beatIdsStr);
        console.log("territory_id (comma-separated IDs):", terrIdsStr);
        console.log("=================================================");

        // Fetch outlets with applied filter parameters (search filters response locally)
        fetchOutletsList(1, beatIdsStr, terrIdsStr, typeIdsStr);

        setFilterModalOpen(false);
    };

    const handleClearFilter = () => {
        setSelectedOutletTypeIds([]);
        setSelectedBeatIds([]);
        setSelectedTerritoryIds([]);
        setOutletTypesCommaStr("");
        setBeatsCommaStr("");
        setTerritoriesCommaStr("");

        console.log("================ FILTER CLEARED ================");

        // Fetch outlets with cleared parameters
        fetchOutletsList(1, "", "", "");
    };

    // Open Unplanned Call Modal
    const handleOpenUnplannedModal = () => {
        setUnplannedModalOpen(true);
        setSelectedOutletIds([]);
        setOutletSearchText("");
        fetchOutletsList(1);
    };

    // Submit Unplanned Call via /api/insertDayPlan
    const handleSubmitUnplannedCall = async () => {
        if (selectedOutletIds.length === 0) {
            toast.warning("Please select at least one outlet.");
            return;
        }
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setSubmittingUnplannedCall(true);
        try {
            const url = `${serverUrl}${API.insertDayPlan}`;
            const posId = localStorage.getItem("positionId") || localStorage.getItem("position_id");
            const empId = localStorage.getItem("employeeId") || localStorage.getItem("employee_id");
            const compId = localStorage.getItem("companyId") || localStorage.getItem("company_id");

            const itemsPayload = selectedOutletIds.map((id) => {
                const cust = outletsList.find((c: any) => {
                    const cId = c.OutletOrgId || c.OutletOrgDataId || c.outlet_org_data_id || c.Id || c.id;
                    return Number(cId) === Number(id);
                });
                const outletOrgDataId = Number(cust?.OutletOrgId || cust?.OutletOrgDataId || cust?.outlet_org_data_id || id);

                const item: any = {
                    DcrDate: moment(selectedDate).format("YYYY-MM-DD"),
                    tp_date: moment(selectedDate).format("YYYY-MM-DD"),
                    OutletOrgDataId: outletOrgDataId,
                    outlet_org_data_id: outletOrgDataId,
                    Agendacontroltype: "FW",
                    agendacontroltype: "FW",
                    PositionId: Number(posId || 0),
                    position_id: Number(posId || 0),
                    beat_id: Number(cust?.BeatId || cust?.beat_id || 0),
                    Itownid: Number(cust?.Itownid || cust?.itownid || cust?.itown_id || 0),
                    itownid: Number(cust?.Itownid || cust?.itownid || cust?.itown_id || 0),
                    Isjw: false,
                    isjw: false,
                    CompanyId: Number(compId || 1),
                    EmployeeId: Number(empId || 0),
                    DcrStatus: "pending",
                    status: "pending",
                    is_planned: false,
                    reason: "ADDED_THROUGH_CUSTOMER",
                    mtp_day_id: 0,
                    DeviceTime: moment().format("YYYY-MM-DD HH:mm:ss"),
                    DeviceMake: "Web Admin",
                    Isprocessed: false
                };

                return item;
            });

            const payload = itemsPayload.length === 1 ? itemsPayload[0] : itemsPayload;
            const res = await postData(url, true, payload);

            if (res.status === 200 || res.data?.statusCode === 200) {
                toast.success(res.data?.message || "Day plan inserted successfully!");
                setUnplannedModalOpen(false);
                setSelectedOutletIds([]);
                fetchFieldWorkData(selectedDate);
                if (onRefreshCounts) onRefreshCounts();
            } else {
                toast.error(res.data?.message || res.data?.error || "Failed to insert day plan");
            }
        } catch (err: any) {
            console.error("Error submitting insertDayPlan:", err);
            toast.error(err?.message || "An error occurred while submitting day plan");
        } finally {
            setSubmittingUnplannedCall(false);
        }
    };

    // Filter outlets by search text locally from API response array (before and after filter)
    const filteredOutlets = outletsList.filter((item: any) => {
        if (!outletSearchText || !outletSearchText.trim()) return true;
        const name = String(item.OutletName || item.outlet_name || item.name || "");
        const code = String(item.OutletOrgCode || item.OutletCode || item.PCode || item.code || "");
        const beat = String(item.BeatName || item.beat_name || "");
        const classification = String(item.Classification || item.OutlettypeName || item.outlet_type_name || "");
        const text = outletSearchText.toLowerCase().trim();
        return name.toLowerCase().includes(text) ||
            code.toLowerCase().includes(text) ||
            beat.toLowerCase().includes(text) ||
            classification.toLowerCase().includes(text);
    });

    const toggleBrandSelection = (cardIndex: number, id: string | number) => {
        setSelectedBrands(prev => {
            const current = prev[cardIndex] || [];
            const updated = current.includes(id) ? current.filter(item => item !== id) : [...current, id];
            return { ...prev, [cardIndex]: updated };
        });
    };

    const toggleDetailingBrandSelection = (cardIndex: number, id: string | number) => {
        setSelectedDetailingBrands(prev => {
            const current = prev[cardIndex] || [];
            const updated = current.includes(id) ? current.filter(item => item !== id) : [...current, id];
            return { ...prev, [cardIndex]: updated };
        });
    };

    const toggleEdPresentationSelection = (cardIndex: number, id: string | number) => {
        setSelectedEdPresentations(prev => {
            const current = prev[cardIndex] || [];
            const updated = current.includes(id) ? current.filter(item => item !== id) : [...current, id];
            return { ...prev, [cardIndex]: updated };
        });
    };

    const toggleSgpiBalanceSelection = (cardIndex: number, id: string | number) => {
        setSelectedSgpiBalances(prev => {
            const current = prev[cardIndex] || [];
            const updated = current.includes(id) ? current.filter(item => item !== id) : [...current, id];
            return { ...prev, [cardIndex]: updated };
        });
    };

    const updateSgpiQuantity = (cardIndex: number, id: string | number, newQty: number, maxBal?: number) => {
        let qty = Math.max(0, newQty);
        if (maxBal !== undefined && maxBal !== null && !isNaN(maxBal)) {
            qty = Math.min(qty, maxBal);
        }
        setSelectedSgpiQuantities(prev => {
            const cardMap = { ...(prev[cardIndex] || {}) };
            if (qty > 0) {
                cardMap[id] = qty;
            } else {
                delete cardMap[id];
            }
            return { ...prev, [cardIndex]: cardMap };
        });
    };

    // Track expanded card indices
    const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});

    // Geofencing Modal State
    const [geoModalOpen, setGeoModalOpen] = useState<boolean>(false);
    const [geoModalConfig, setGeoModalConfig] = useState<{
        title: string;
        message: string;
        type: 'gps_not_found' | 'restriction' | 'permission' | 'override_available';
        outletOrgId?: any;
        targetIdx?: number;
    }>({ title: '', message: '', type: 'restriction' });
    const [verifyingLocation, setVerifyingLocation] = useState<boolean>(false);

    const toggleCard = (index: number) => {
        setExpandedCards(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleReportCallWithGeofencing = async (index: number, item: any) => {
        // If already expanded, just toggle close
        if (expandedCards[index]) {
            toggleCard(index);
            return;
        }

        const enableGeoFencing = String(getSystemConfig("enable_geo_fencing") || "").trim().toLowerCase();
        const isGeoActive = enableGeoFencing === 'yes' || enableGeoFencing === 'on' || enableGeoFencing === 'true' || enableGeoFencing === '1';

        // If geofencing system config is NOT active, open modal directly for submission without API validation
        if (!isGeoActive) {
            toggleCard(index);
            return;
        }

        // If geofencing is enabled, validate location via API first
        const outletOrgId =
            item?.OutletOrgDataId ??
            item?.outlet_org_data_id ??
            item?.OutletOrgId ??
            item?.outlet_org_id ??
            "";

        setVerifyingLocation(true);
        try {
            const check = await validateGeofencingForDCR(outletOrgId);
            if (check.allowed) {
                toggleCard(index);
            } else if (check.approvalFlow) {
                toast.warning(check.message || "Data is going for approval flow.");
            } else if (check.gpsNotFound) {
                setGeoModalConfig({
                    title: "Confirm Outlet GPS Location",
                    message: check.message || "The selected location will be used as the GPS location for this outlet. Do you want to continue?",
                    type: "gps_not_found",
                    outletOrgId,
                    targetIdx: index,
                });
                setGeoModalOpen(true);
            } else if (check.locationPromptRequired) {
                setGeoModalConfig({
                    title: "Location Permission Required",
                    message: check.message || "Browser location access is required to report DCR.",
                    type: "permission",
                });
                setGeoModalOpen(true);
            } else {
                setGeoModalConfig({
                    title: "Location Restriction",
                    message: check.message || "You are outside the assigned geofence for this outlet.",
                    type: check.geoOverrideAvailable ? "override_available" : "restriction",
                    outletOrgId,
                    targetIdx: index,
                });
                setGeoModalOpen(true);
            }
        } catch (err: any) {
            console.error("Geofencing error:", err);
            toggleCard(index);
        } finally {
            setVerifyingLocation(false);
        }
    };

    const handleConfirmGpsOverride = async () => {
        if (!geoModalConfig.outletOrgId || geoModalConfig.targetIdx === undefined) return;
        setVerifyingLocation(true);
        try {
            const check = await validateGeofencingForDCR(geoModalConfig.outletOrgId, true);
            if (check.allowed) {
                toast.success(check.message || "Location updated successfully!");
                setGeoModalOpen(false);
                toggleCard(geoModalConfig.targetIdx);
            } else {
                // If approval flow or 400 Bad Request (e.g. "Data is going for approval flow.")
                toast.warning(check.message || "Data is going for approval flow.");
                setGeoModalOpen(false);
            }
        } catch (e: any) {
            toast.error("Failed to update location.");
            setGeoModalOpen(false);
        } finally {
            setVerifyingLocation(false);
        }
    };

    // Track active step tab (1 to 4) per card modal
    const [activeFwSteps, setActiveFwSteps] = useState<Record<number, number>>({});

    const getActiveStep = (idx: number) => activeFwSteps[idx] || 1;
    const setStep = (idx: number, step: number) => {
        setActiveFwSteps(prev => ({ ...prev, [idx]: Math.min(Math.max(step, 1), 3) }));
    };

    // Helper to parse bridge responses structured like { data: { page: 1, total_pages: 1, data: [...] } }
    const parseBridgeResponse = (res: any) => {
        let payload = res?.data;
        if (payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
            payload = payload.data;
        }
        const items = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.items)
                ? payload.items
                : Array.isArray(payload?.records)
                    ? payload.records
                    : Array.isArray(payload)
                        ? payload
                        : [];

        const page = Number(payload?.page) || 1;
        const totalPages = Number(payload?.total_pages) || 1;
        const totalCount = Number(payload?.total_count) || items.length;

        return { items, page, totalPages, totalCount };
    };

    const fetchBrands = async (page: number = 1) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingBrands(true);
        try {
            const res = await getData(`${serverUrl}${API.getBrands}`, true, { page, limit: 50 });
            const { items, page: currPage, totalPages } = parseBridgeResponse(res);
            setBrandsList(items);
            setBrandsPage(currPage);
            setBrandsTotalPages(totalPages);
        } catch (err) {
            console.error("Error fetching brands:", err);
        } finally {
            setLoadingBrands(false);
        }
    };

    const fetchEdPresentations = async (page: number = 1) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingEd(true);
        try {
            const res = await getData(`${serverUrl}${API.getEdPresentation}`, true, { page, limit: 50 });
            const { items, page: currPage, totalPages } = parseBridgeResponse(res);
            setEdPresentationList(items);
            setEdPage(currPage);
            setEdTotalPages(totalPages);
        } catch (err) {
            console.error("Error fetching ed presentations:", err);
        } finally {
            setLoadingEd(false);
        }
    };

    const fetchSgpiBalance = async (page: number = 1) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingSgpi(true);
        try {
            const res = await getData(`${serverUrl}${API.getSgpiBalance}`, true, { page, limit: 50 });
            const { items, page: currPage, totalPages } = parseBridgeResponse(res);
            setSgpiBalanceList(items);
            setSgpiPage(currPage);
            setSgpiTotalPages(totalPages);
        } catch (err) {
            console.error("Error fetching sgpi balance:", err);
        } finally {
            setLoadingSgpi(false);
        }
    };

    const fetchProfileReporting = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingReporting(true);
        try {
            let empId = localStorage.getItem("employeeId") || localStorage.getItem("employee_id") || localStorage.getItem("empId");
            if (!empId || empId === 'null' || empId === 'undefined') {
                try {
                    await ensureDexieReady();
                    const rawEmpData = await getDataFromDexie('Employee');
                    if (rawEmpData && rawEmpData[0]?.EmployeeId) {
                        empId = rawEmpData[0].EmployeeId;
                    }
                } catch (e) {
                    console.warn("Could not read Employee from Dexie:", e);
                }
            }

            const url = `${serverUrl}${API.getProfile}`;
            const params: any = {};
            if (empId && empId !== 'null' && empId !== 'undefined') {
                params.employee_id = empId;
            }
            const res = await getData(url, true, params);
            console.log("Profile Data:", res?.data);
            const rData = res?.data?.data?.Reporting || res?.data?.Reporting || [];
            setProfileReportingList(Array.isArray(rData) ? rData : []);
        } catch (err) {
            console.error("Error fetching profile reporting data:", err);
            setProfileReportingList([]);
        } finally {
            setLoadingReporting(false);
        }
    };

    const fetchBridgeData = async () => {
        setLoadingBridgeData(true);
        await Promise.allSettled([
            fetchBrands(1),
            fetchEdPresentations(1),
            fetchSgpiBalance(1),
            fetchProfileReporting()
        ]);
        setLoadingBridgeData(false);
    };

    // Filter brands from /api/getBrands that have a matching BrandId in /api/getEdPresentation
    const detailingBrandsList = React.useMemo(() => {
        const edBrandIds = new Set<string>();
        (edPresentationList || []).forEach((ed: any) => {
            const bId = ed.BrandId ?? ed.brand_id ?? ed.brandid;
            if (bId !== undefined && bId !== null && bId !== '') {
                edBrandIds.add(String(bId));
            }
        });

        return (brandsList || []).filter((b: any) => {
            const bId = b.BrandId ?? b.id ?? b.brand_id;
            return bId !== undefined && bId !== null && edBrandIds.has(String(bId));
        });
    }, [brandsList, edPresentationList]);

    // Categorize SGPI Balance items into Active vs Previous (Samples, Gifts, Promo, Others)
    const sgpiCategorized = React.useMemo(() => {
        const headerDateMom = moment(selectedDate);

        const activeList: any[] = [];
        const previousList: any[] = [];

        (sgpiBalanceList || []).forEach((row: any) => {
            const hasStart = !!row.UseStartDate;
            const hasEnd = !!row.UseEndDate;
            const start = hasStart ? moment(row.UseStartDate) : null;
            const end = hasEnd ? moment(row.UseEndDate) : null;

            const isValidDate = (!hasStart || !hasEnd) || (
                start && end && headerDateMom.isSameOrAfter(start, 'day') && headerDateMom.isSameOrBefore(end, 'day')
            );

            if (isValidDate) {
                activeList.push(row);
            } else {
                previousList.push(row);
            }
        });

        const activeSamples: any[] = [];
        const activeGifts: any[] = [];
        const activePromo: any[] = [];
        const activeOthers: any[] = [];

        activeList.forEach((item: any) => {
            const type = (item.SgpiType || item.sgpi_type || item.type || '').toLowerCase();
            if (type === 'samples' || type === 'sample') activeSamples.push(item);
            else if (type === 'gifts' || type === 'gift') activeGifts.push(item);
            else if (type === 'promo' || type === 'promotion' || type === 'promotional') activePromo.push(item);
            else activeOthers.push(item);
        });

        const previousSamples: any[] = [];
        const previousGifts: any[] = [];
        const previousPromo: any[] = [];
        const previousOthers: any[] = [];

        previousList.forEach((item: any) => {
            const type = (item.SgpiType || item.sgpi_type || item.type || '').toLowerCase();
            if (type === 'samples' || type === 'sample') previousSamples.push(item);
            else if (type === 'gifts' || type === 'gift') previousGifts.push(item);
            else if (type === 'promo' || type === 'promotion' || type === 'promotional') previousPromo.push(item);
            else previousOthers.push(item);
        });

        return {
            activeSamples,
            activeGifts,
            activePromo,
            activeOthers,
            previousSamples,
            previousGifts,
            previousPromo,
            previousOthers
        };
    }, [sgpiBalanceList, selectedDate]);

    const fetchFieldWorkData = async (targetDate: string) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingFwData(true);
        try {
            const formattedDateParam = moment(targetDate).format('YYYY-MM-DD');
            const url = `${serverUrl}${API.getFieldWork}`;
            const response = await getData(url, true, { date: formattedDateParam });
            console.log("Field Work API response:", response);
            if (response.status === 200 || response.data?.statusCode === 200) {
                let list: any[] = [];
                const d = response.data;
                const payload = d?.data || d;

                const dailycallsRaw = Array.isArray(payload?.dailycalls) ? payload.dailycalls : [];
                const dayplanRaw = Array.isArray(payload?.dayplan) ? payload.dayplan : [];

                const isJwItem = (item: any) => {
                    const isjwVal = item.Isjw ?? item.isjw;
                    return isjwVal === true || isjwVal === 'true' || isjwVal === 1 || isjwVal === '1';
                };

                const dailycallsFormatted = dailycallsRaw
                    .map((item: any) => ({
                        ...item,
                        isDailyCall: true,
                        dcr_status: item.dcr_status ?? item.DcrStatus ?? 'Reported',
                        DcrStatus: item.DcrStatus ?? item.dcr_status ?? 'Reported'
                    }));

                const dayplanFormatted = dayplanRaw
                    .filter((item: any) => `${item.DcrStatus ?? item.dcr_status ?? item.status ?? ''}`.toLowerCase() !== 'completed')
                    .map((item: any) => ({
                        ...item,
                        isDailyCall: false
                    }));

                if (payload?.dayplan !== undefined || payload?.dailycalls !== undefined) {
                    list = [...dayplanFormatted, ...dailycallsFormatted];
                } else if (Array.isArray(payload)) {
                    list = payload;
                } else if (Array.isArray(payload?.items)) {
                    list = payload.items;
                } else if (Array.isArray(payload?.data)) {
                    list = payload.data;
                } else if (payload && typeof payload === 'object' && !Array.isArray(payload) && (payload.DcrId || payload.dcr_id || payload.DayPlanId || payload.day_plan_id || payload.OutletOrgDataId || payload.outlet_org_data_id)) {
                    list = [payload];
                } else {
                    list = [];
                }

                const formattedList = list.map((item: any) => {
                    const rawStatus = item.isDailyCall ? 'Reported' : (item.dcr_status ?? item.DcrStatus ?? item.status ?? 'pending');
                    return {
                        ...item,
                        DcrId: item.DcrId ?? item.dcr_id ?? item.id,
                        dcr_id: item.dcr_id ?? item.DcrId ?? item.id,
                        DayPlanId: item.DayPlanId ?? item.day_plan_id ?? item.DayplanId,
                        OutletOrgDataId: item.OutletOrgDataId ?? item.outlet_org_data_id ?? item.OutletOrgId ?? item.outlet?.outlet_org_data_id ?? item.outlet?.id,
                        PositionId: item.PositionId ?? item.position_id,
                        Agendacontroltype: item.Agendacontroltype ?? item.agendacontroltype ?? 'FW',
                        agendacontroltype: item.agendacontroltype ?? item.Agendacontroltype ?? 'FW',
                        Itownid: item.Itownid ?? item.itownid ?? item.itown_id ?? item.outlet?.itownid,
                        Isjw: item.Isjw ?? item.isjw ?? false,
                        DcrDate: item.DcrDate ?? item.dcr_date ?? item.tp_date ?? formattedDateParam,
                        CompanyId: item.CompanyId ?? item.company_id,
                        Managers: item.Managers ?? item.managers ?? '',
                        managers: item.managers ?? item.Managers ?? '',
                        SgpiOut: item.SgpiOut ?? item.sgpi_out ?? '',
                        OutletFeedback: item.OutletFeedback ?? item.outlet_feedback ?? [],
                        outlet_feedback: item.outlet_feedback ?? item.OutletFeedback ?? [],
                        BrandsDetailed: item.BrandsDetailed ?? item.brands_detailed ?? [],
                        EmployeeFeedback: Array.isArray(item.EmployeeFeedback) && item.EmployeeFeedback.length > 0
                            ? item.EmployeeFeedback
                            : (Array.isArray(item.employee_feedback) && item.employee_feedback.length > 0
                                ? item.employee_feedback
                                : [{ Question: "Were you with the team member on the JFW call?", Answer: "Yes" }]),
                        employee_feedback: Array.isArray(item.employee_feedback) && item.employee_feedback.length > 0
                            ? item.employee_feedback
                            : (Array.isArray(item.EmployeeFeedback) && item.EmployeeFeedback.length > 0
                                ? item.EmployeeFeedback
                                : [{ Question: "Were you with the team member on the JFW call?", Answer: "Yes" }]),
                        NcaComments: item.NcaComments ?? item.nca_comments ?? item.remark ?? '',
                        nca_comments: item.nca_comments ?? item.NcaComments ?? item.remark ?? '',
                        DcrStatus: rawStatus,
                        dcr_status: rawStatus,
                        EmployeeId: item.EmployeeId ?? item.employee_id
                    };
                });

                const initialDetailingBrands: Record<number, (string | number)[]> = {};
                const initialReportingManagers: Record<number, (string | number)[]> = {};
                const initialSgpiBalances: Record<number, (string | number)[]> = {};
                const initialSgpiQuantities: Record<number, Record<string | number, number>> = {};

                formattedList.forEach((item: any, idx: number) => {
                    // Pre-populate Detailing Brands
                    let bIds: (string | number)[] = [];
                    const rawBrands = item.BrandsDetailed ?? item.brands_detailed;
                    if (typeof rawBrands === 'string' && rawBrands.trim()) {
                        bIds = rawBrands.split(',').map(s => s.trim()).filter(Boolean);
                    } else if (Array.isArray(rawBrands)) {
                        bIds = rawBrands.map((b: any) => typeof b === 'object' ? (b.BrandId ?? b.brand_id ?? b.id ?? b) : b).filter(Boolean);
                    }
                    if (bIds.length > 0) initialDetailingBrands[idx] = bIds;

                    // Pre-populate Reporting Managers
                    let mIds: (string | number)[] = [];
                    const rawMgrs = item.Managers ?? item.managers;
                    if (typeof rawMgrs === 'string' && rawMgrs.trim()) {
                        mIds = rawMgrs.split(',').map(s => s.trim()).filter(Boolean);
                    } else if (Array.isArray(rawMgrs)) {
                        mIds = rawMgrs.map((m: any) => typeof m === 'object' ? (m.EmployeeId ?? m.employee_id ?? m.id ?? m) : m).filter(Boolean);
                    }
                    if (mIds.length > 0) initialReportingManagers[idx] = mIds;

                    // Pre-populate SGPI Out / Balances & Quantities
                    let sgpiArr: any[] = [];
                    const rawSgpi = item.SgpiOut ?? item.sgpi_out;
                    if (typeof rawSgpi === 'string' && rawSgpi.trim()) {
                        try {
                            const parsed = JSON.parse(rawSgpi);
                            if (Array.isArray(parsed)) sgpiArr = parsed;
                        } catch (e) {
                            console.warn("Could not parse SgpiOut JSON:", e);
                        }
                    } else if (Array.isArray(rawSgpi)) {
                        sgpiArr = rawSgpi;
                    }

                    const balIds: (string | number)[] = [];
                    const quantities: Record<string | number, number> = {};
                    sgpiArr.forEach((s: any) => {
                        const sId = s.SgpiId ?? s.sgpi_id ?? s.id ?? s.code;
                        const qty = Number(s.Quantity ?? s.quantity ?? s.qty ?? s.Qty ?? 0);
                        if (sId !== undefined && sId !== null && qty > 0) {
                            balIds.push(sId);
                            quantities[sId] = qty;
                        }
                    });
                    if (balIds.length > 0) {
                        initialSgpiBalances[idx] = balIds;
                        initialSgpiQuantities[idx] = quantities;
                    }
                });

                setSelectedDetailingBrands(initialDetailingBrands);
                setSelectedReportingManagers(initialReportingManagers);
                setSelectedSgpiBalances(initialSgpiBalances);
                setSelectedSgpiQuantities(initialSgpiQuantities);

                setFwList(formattedList);
            } else {
                setFwList([]);
            }
        } catch (error) {
            console.error("Error fetching field work data:", error);
            setFwList([]);
        } finally {
            setLoadingFwData(false);
        }
    };

    useEffect(() => {
        const activeDate = location.state?.date || reduxSelectedDate || localStorage.getItem("selectedDate") || moment().format('YYYY-MM-DD');
        if (activeDate !== selectedDate) {
            setSelectedDate(activeDate);
        }
    }, [reduxSelectedDate, location.state?.date]);

    useEffect(() => {
        if (selectedDate) {
            if (selectedDate !== reduxSelectedDate) {
                dispatch(setReduxSelectedDate(selectedDate));
            }
            fetchFieldWorkData(selectedDate);
            fetchBridgeData();
        }
    }, [selectedDate]);

    const handleUpdateFieldWork = async (targetIdx?: number) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        if (!fwList || fwList.length === 0) {
            toast.error("No field work records to update");
            return;
        }

        const targetList = typeof targetIdx === 'number' ? [fwList[targetIdx]] : fwList;

        if (typeof targetIdx === 'number') {
            setSubmittingFwIndex(targetIdx);
        } else {
            setSubmittingFwData(true);
        }

        try {
            const url = `${serverUrl}${API.updateFieldWork}`;
            const posId = localStorage.getItem("positionId") || localStorage.getItem("position_id");
            const empId = localStorage.getItem("employeeId") || localStorage.getItem("employee_id");
            const compId = localStorage.getItem("companyId") || localStorage.getItem("company_id");

            const itemsPayload = targetList.map((item: any) => {
                const idx = fwList.indexOf(item);
                // Collect selected SGPI quantities for this card index (matching app logic: JSON array string of SgpiId & qty)
                const sgpiMap = selectedSgpiQuantities[idx] || {};
                const sgpiOutArr = Object.entries(sgpiMap)
                    .filter(([_, qty]) => Number(qty) > 0)
                    .map(([sId, qty]) => ({ SgpiId: Number(sId), qty: Number(qty), Quantity: Number(qty) }));
                const sgpiOutStr = sgpiOutArr.length > 0 ? JSON.stringify(sgpiOutArr) : (typeof item.SgpiOut === 'string' ? item.SgpiOut : JSON.stringify(item.SgpiOut || item.sgpi_out || []));

                // Collect selected detailing brands for this card index (matching app logic: comma-separated string)
                const detailingBrandIds = selectedDetailingBrands[idx] || [];
                const brandsDetailedStr = detailingBrandIds.length > 0
                    ? detailingBrandIds.join(',')
                    : (Array.isArray(item.BrandsDetailed)
                        ? item.BrandsDetailed.map((b: any) => b.BrandId ?? b.brand_id ?? b).join(',')
                        : String(item.BrandsDetailed ?? item.brands_detailed ?? ""));

                // Collect selected reporting managers for this card index (matching app logic: comma-separated employee IDs)
                const mgrIds = selectedReportingManagers[idx] || [];
                const managersStr = mgrIds.length > 0
                    ? mgrIds.join(',')
                    : String(item.Managers ?? item.managers ?? "");

                const payloadItem: any = {
                    DcrId: item.DcrId ? Number(item.DcrId) : (item.dcr_id ? Number(item.dcr_id) : undefined),
                    DayPlanId: item.DayPlanId ? Number(item.DayPlanId) : (item.day_plan_id ? Number(item.day_plan_id) : undefined),
                    OutletOrgDataId: Number(item.OutletOrgDataId ?? item.outlet_org_data_id ?? item.OutletOrgId ?? item.outlet?.outlet_org_data_id ?? item.outlet?.id ?? 0),
                    PositionId: Number(item.PositionId ?? item.position_id ?? posId ?? 0),
                    Agendacontroltype: item.Agendacontroltype ?? item.agendacontroltype ?? "FW",
                    Itownid: String(item.Itownid ?? item.itownid ?? item.itown_id ?? item.outlet?.itownid ?? "70"),
                    Isjw: Boolean(mgrIds.length > 0 || item.Isjw || item.isjw),
                    DcrDate: moment(selectedDate).format("YYYY-MM-DD"),
                    CompanyId: Number(item.CompanyId ?? item.company_id ?? compId ?? 1),
                    Managers: managersStr,
                    SgpiOut: sgpiOutStr,
                    OutletFeedback: typeof item.OutletFeedback === 'string' ? item.OutletFeedback : JSON.stringify(item.OutletFeedback ?? item.outlet_feedback ?? []),
                    BrandsDetailed: brandsDetailedStr,
                    NcaComments: String(item.NcaComments ?? item.nca_comments ?? item.remark ?? ""),
                    DeviceTime: moment().format("YYYY-MM-DD HH:mm:ss"),
                    Isprocessed: false,
                    EmployeeId: Number(item.EmployeeId ?? item.employee_id ?? empId ?? 0),
                    DeviceMake: "Web Admin",
                    DcrStatus: "Reported",
                    latitude: String(item.latitude ?? ""),
                    longitude: String(item.longitude ?? ""),
                    DcrReportedTime: moment().format('YYYY-MM-DD HH:mm:ss.SSS')
                };

                return payloadItem;
            });

            const payload = itemsPayload.length === 1 ? itemsPayload[0] : { items: itemsPayload };
            const response = await postData(url, true, payload);

            if (response.status === 200 || response.data?.statusCode === 200 || response.data?.status === 200 || response.data?.success) {
                toast.success(response.data?.message || "Field work record updated successfully!");
                if (typeof targetIdx === 'number') {
                    setExpandedCards(prev => ({ ...prev, [targetIdx]: false }));
                } else {
                    setExpandedCards({});
                }
                await fetchFieldWorkData(selectedDate);
                if (onRefreshCounts) onRefreshCounts();
            } else {
                toast.error(response.data?.message || response.data?.error || "Failed to update field work");
            }
        } catch (error: any) {
            console.error("Field work update error:", error);
            toast.error(error?.message || "An error occurred while updating field work");
        } finally {
            setSubmittingFwData(false);
            setSubmittingFwIndex(null);
        }
    };

    const getStatusBadgeColor = (status: string) => {
        switch (`${status || ''}`.toLowerCase()) {
            case 'completed':
            case 'reported':
                return 'success';
            case 'awatingfeedback':
            case 'pending':
            case 'in_progress':
                return 'warning';
            case 'rejected':
            case 'cancelled':
                return 'danger';
            default:
                return 'primary';
        }
    };

    const getItemCategory = (item: any): 'Doctor' | 'Pharmacy' | 'Stockist' => {
        const rawType = (
            item.outlet?.OutlettypeName ||
            item.outlet?.outlet_type_name ||
            item.outlet?.outlet_type ||
            item.outlet?.Classification ||
            item.outlet_type ||
            item.OutletType ||
            item.OutlettypeName ||
            item.classification ||
            ''
        ).toLowerCase();

        if (rawType.includes('pharm') || rawType.includes('chem') || rawType.includes('drug') || rawType.includes('retail')) {
            return 'Pharmacy';
        }
        if (rawType.includes('stock') || rawType.includes('dist') || rawType.includes('wholesal')) {
            return 'Stockist';
        }
        return 'Doctor';
    };

    const doctorItems = fwList.filter(item => getItemCategory(item) === 'Doctor');
    const pharmacyItems = fwList.filter(item => getItemCategory(item) === 'Pharmacy');
    const stockistItems = fwList.filter(item => getItemCategory(item) === 'Stockist');

    const doctorReported = doctorItems.filter(it => `${it.dcr_status || it.DcrStatus || ''}`.toLowerCase() === 'reported').length;
    const pharmacyReported = pharmacyItems.filter(it => `${it.dcr_status || it.DcrStatus || ''}`.toLowerCase() === 'reported').length;
    const stockistReported = stockistItems.filter(it => `${it.dcr_status || it.DcrStatus || ''}`.toLowerCase() === 'reported').length;

    const displayedList = activeCategory === 'Doctor'
        ? doctorItems
        : activeCategory === 'Pharmacy'
            ? pharmacyItems
            : activeCategory === 'Stockist'
                ? stockistItems
                : fwList;

    return (
        <Fragment>
            <Container fluid className="p-4 bg-light min-vh-100">
                <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
                    <div className="card-header bg-white border-bottom p-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-2">
                            <div className="d-flex align-items-center gap-3">
                                <Button
                                    color="link"
                                    className="p-0 text-dark text-decoration-none d-flex align-items-center gap-2"
                                    onClick={() => {
                                        dispatch(setReduxSelectedDate(selectedDate));
                                        navigate('/me', { state: { date: selectedDate } });
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-center bg-primary text-white rounded p-1" style={{ width: 28, height: 28 }}>
                                        <FeatherIconCom iconName="ArrowLeft" size={16} />
                                    </div>
                                    <span className="fw-bold fs-5 text-dark">
                                        {moment(selectedDate).format('DD MMM')}'s Agenda
                                    </span>
                                </Button>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    color="success"
                                    className="d-flex align-items-center gap-1 px-3 py-2 fw-semibold text-white border-0 shadow-sm"
                                    style={{ backgroundColor: '#198754', borderColor: '#198754', borderRadius: '8px', fontSize: '0.85rem' }}
                                    onClick={handleOpenUnplannedModal}
                                >
                                    <FeatherIconCom iconName="Plus" size={15} />
                                    <span>Unplanned Call</span>
                                </Button>
                                <Button
                                    color="dark"
                                    outline
                                    className="d-flex align-items-center gap-1 px-3 py-2 fw-semibold btn-back-to-me"
                                    style={{ borderRadius: '8px', fontSize: '0.85rem' }}
                                    onClick={() => {
                                        dispatch(setReduxSelectedDate(selectedDate));
                                        navigate('/me', { state: { date: selectedDate } });
                                    }}
                                >
                                    <FeatherIconCom iconName="ArrowLeft" size={15} />
                                    <span>Back to ME</span>
                                </Button>
                            </div>
                        </div>

                        {/* Agenda Tabs matching 3rd Image (No Mtp_Deviation) */}
                        <div className="d-flex align-items-center gap-4 mt-3 border-bottom pb-2 flex-wrap" style={{ fontSize: '0.95rem' }}>
                            <span
                                className="fw-bold text-muted cursor-pointer"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    if (onTabChange) onTabChange('JW');
                                    else navigate('/me/JW', { state: { date: selectedDate } });
                                }}
                            >
                                Joint Working {agendaCounts ? `- ${agendaCounts.jw}` : ''}
                            </span>
                            <span
                                className={`fw-bold cursor-pointer ${activeCategory === 'Doctor' ? 'text-primary border-bottom border-primary border-3 pb-2 mb-n2' : 'text-muted'}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveCategory('Doctor')}
                            >
                                Doctor - {agendaCounts ? agendaCounts.doctorReported : doctorReported} / {agendaCounts ? agendaCounts.doctorTotal : doctorItems.length}
                            </span>
                            <span
                                className={`fw-bold cursor-pointer ${activeCategory === 'Pharmacy' ? 'text-primary border-bottom border-primary border-3 pb-2 mb-n2' : 'text-muted'}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveCategory('Pharmacy')}
                            >
                                Pharmacy - {agendaCounts ? agendaCounts.pharmacyReported : pharmacyReported} / {agendaCounts ? agendaCounts.pharmacyTotal : pharmacyItems.length}
                            </span>
                            <span
                                className={`fw-bold cursor-pointer ${activeCategory === 'Stockist' ? 'text-primary border-bottom border-primary border-3 pb-2 mb-n2' : 'text-muted'}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveCategory('Stockist')}
                            >
                                Stockist - {agendaCounts ? agendaCounts.stockistReported : stockistReported} / {agendaCounts ? agendaCounts.stockistTotal : stockistItems.length}
                            </span>
                            <span
                                className="fw-bold text-muted cursor-pointer"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    if (onTabChange) onTabChange('NCA');
                                    else navigate('/me/NCA', { state: { date: selectedDate } });
                                }}
                            >
                                {getLabel("NCA")} {agendaCounts ? `- ${agendaCounts.nca}` : ''}
                            </span>
                        </div>
                    </div>

                    <CardBody className="p-4" style={{ color: '#000000' }}>
                        {loadingFwData ? (
                            <Loader height="250px" />
                        ) : displayedList.length > 0 ? (
                            <Row className="g-3">
                                {displayedList.map((item: any, listIdx: number) => {
                                    const originalIndex = fwList.indexOf(item);
                                    const idx = originalIndex >= 0 ? originalIndex : listIdx;
                                    const dcrId = item.dcr_id ?? (idx + 1);
                                    const empName = item.employee_details?.name || item.mr_name || 'N/A';
                                    const empDesignation = item.employee_details?.designation || item.mr_details?.designation || '';

                                    const outletName = item.outlet?.outlet_name || item.outlet_name || 'N/A';
                                    const outletType = item.outlet?.qualification || item.outlet?.speciality || item.outlet?.outlet_type || item.qualification || 'Doctor';
                                    const outletCode = item.outlet?.outlet_code || item.outlet?.pcode || item.pcode || item.outlet_code || item.OutletOrgDataId || 'N/A';
                                    const outletAddress = item.outlet?.outlet_address || '';
                                    const townName = item.outlet?.town_name || item.town_name || '';
                                    const campaignName = item.outlet?.campaign_name || item.outlet?.sgpi_name || item.sgpi_name || 'REAZ SGPI';
                                    const rcpaValue = item.rcpa_value || item.outlet?.rcpa_value || 'NA';
                                    const lastVisited = item.last_visited_date || item.outlet?.last_visited_on || moment(selectedDate).format('DD-MM-YYYY');
                                    const vfCount = item.vf_count || item.visit_frequency || item.outlet?.visit_frequency || '0/2 VF';

                                    const agendaType = item.agendacontroltype || 'FW';
                                    const dcrStatus = item.dcr_status || item.DcrStatus || 'Reported';
                                    const isReported = `${dcrStatus}`.toLowerCase() === 'reported';
                                    const ncaComments = item.nca_comments || '';

                                    const isExpanded = !!expandedCards[idx];

                                    return (
                                        <Col xs={12} md={6} lg={4} key={idx}>
                                            <Card className="border h-100 shadow-sm" style={{ borderRadius: '10px', overflow: 'hidden', borderColor: '#dbeafe', backgroundColor: '#ffffff' }}>
                                                {/* Top section matching 3rd Image */}
                                                <div className="p-3" style={{ backgroundColor: '#eef5ff', borderBottom: '1px solid #e2e8f0' }}>
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h6 className="fw-bold text-dark mb-0 text-truncate me-2" style={{ fontSize: '1.02rem', color: '#1e293b' }} title={outletName}>
                                                            {outletName}
                                                        </h6>
                                                        <span
                                                            className="badge px-2 py-1 fw-bold text-white shadow-none"
                                                            style={{ backgroundColor: '#1d4ed8', borderRadius: '4px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                                        >
                                                            {vfCount}
                                                        </span>
                                                    </div>

                                                    <div className="d-flex align-items-center gap-3 mb-2 flex-wrap" style={{ fontSize: '0.85rem' }}>
                                                        <div className="d-flex align-items-center gap-1 text-secondary fw-semibold">
                                                            <FeatherIconCom iconName="User" size={13} className="text-primary" />
                                                            <span>{outletType}</span>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-1 text-secondary fw-semibold">
                                                            <span className="text-primary" style={{ fontSize: '0.9rem', lineHeight: 1 }}>★</span>
                                                            <span>{campaignName}</span>
                                                        </div>
                                                    </div>

                                                    {/* <div className="text-muted mb-2" style={{ fontSize: '0.83rem' }}>
                                                        Last Visit Rcpa Value: <span className="text-dark fw-semibold">{rcpaValue}</span>
                                                    </div> */}

                                                    <div className="d-flex align-items-center gap-1 text-muted mb-3" style={{ fontSize: '0.83rem' }}>
                                                        <span className="d-inline-flex align-items-center justify-content-center rounded-circle border border-danger text-danger me-1" style={{ width: 16, height: 16, fontSize: '0.65rem' }}>
                                                            <FeatherIconCom iconName="Clock" size={10} />
                                                        </span>
                                                        <span>Last visited on {lastVisited}</span>
                                                    </div>

                                                    <div className="d-flex justify-content-between align-items-center pt-2 border-top" style={{ borderColor: '#dbeafe' }}>
                                                        <div className="small text-muted" style={{ fontSize: '0.83rem' }}>
                                                            PCode: <span className="text-dark fw-semibold">{outletCode}</span>
                                                        </div>
                                                        {townName && (
                                                            <span
                                                                className="badge px-2 py-1 text-white fw-bold shadow-none"
                                                                style={{ backgroundColor: '#0284c7', borderRadius: '4px', fontSize: '0.75rem' }}
                                                            >
                                                                {townName}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Card Footer matching 3rd Image (No Skip Visit or Reschedule) */}
                                                <div className="p-2 border-top bg-white d-flex justify-content-between align-items-center" style={{ minHeight: '44px' }}>
                                                    {isReported ? (
                                                        <div className="w-100 py-1 d-flex justify-content-center align-items-center px-2">
                                                            <span className="text-success fw-bold d-flex align-items-center gap-1" style={{ fontSize: '0.9rem', color: '#16a34a' }}>
                                                                <FeatherIconCom iconName="Check" size={16} />
                                                                <span>DCR Reported</span>
                                                            </span>
                                                            {/* <Button
                                                                color="link"
                                                                size="sm"
                                                                className="p-0 text-decoration-none text-muted fw-semibold"
                                                                onClick={() => toggleCard(idx)}
                                                            >
                                                                {isExpanded ? 'Hide Details' : 'View Details'}
                                                            </Button> */}
                                                        </div>
                                                    ) : (
                                                        <div className="w-100 d-flex justify-content-between align-items-center px-2 py-1">
                                                            <span
                                                                className="badge px-2 py-1 fw-bold text-white shadow-none"
                                                                style={{ backgroundColor: '#64748b', borderRadius: '4px', fontSize: '0.75rem' }}
                                                            >
                                                                Pending
                                                            </span>
                                                            <Button
                                                                color="success"
                                                                size="sm"
                                                                className="d-flex align-items-center gap-1 px-3 py-1 fw-bold text-white shadow-sm border-0"
                                                                style={{ backgroundColor: '#198754', borderColor: '#198754', borderRadius: '6px', fontSize: '0.8rem' }}
                                                                onClick={() => handleReportCallWithGeofencing(idx, item)}
                                                                disabled={verifyingLocation}
                                                            >
                                                                {verifyingLocation ? <Spinner size="sm" color="light" /> : (
                                                                    <>
                                                                        <span>{isExpanded ? 'Close Detailing' : 'Report Call'}</span>
                                                                        <FeatherIconCom iconName={isExpanded ? "ChevronUp" : "ChevronDown"} size={14} />
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* DCR Report / View Modal Popup */}
                                                <Modal isOpen={isExpanded} toggle={() => toggleCard(idx)} size="lg" centered scrollable>
                                                    <ModalHeader toggle={() => toggleCard(idx)} className="bg-light border-bottom">
                                                        <div>
                                                            <h5 className="fw-bold text-dark mb-0">{outletName}</h5>
                                                            <small className="text-muted">{outletType} • {townName} • PCode: {outletCode}</small>
                                                        </div>
                                                    </ModalHeader>
                                                    <ModalBody className="p-4 bg-white" style={{ color: '#000000' }}>




                                                        {/* Brands Selection - Commented out as requested */}
                                                        {/* <div className="mb-4 pt-2 border-top">
                                                            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                                                                <label className="form-label small fw-bold text-dark mb-0" style={{ color: '#000000' }}>
                                                                    <FeatherIconCom iconName="Tag" size={14} className="me-1 text-primary" />
                                                                    Brands Selection (/api/getBrands)
                                                                </label>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Badge color="primary" pill className="px-2 py-1">
                                                                        {(selectedBrands[idx] || []).length} Selected
                                                                    </Badge>
                                                                    <div className="d-flex align-items-center gap-1 border rounded px-2 py-1 bg-white shadow-sm">
                                                                        <span className="small text-muted me-1 fw-semibold" style={{ fontSize: '0.78rem' }}>
                                                                            Page {brandsPage} of {brandsTotalPages}
                                                                        </span>
                                                                        <Button
                                                                            color="light"
                                                                            size="sm"
                                                                            disabled={brandsPage <= 1 || loadingBrands}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                fetchBrands(brandsPage - 1);
                                                                            }}
                                                                            className="p-0 px-1 border-0 shadow-none text-dark bg-transparent"
                                                                            title="Previous Page"
                                                                        >
                                                                            <FeatherIconCom iconName="ChevronLeft" size={14} />
                                                                        </Button>
                                                                        <Button
                                                                            color="light"
                                                                            size="sm"
                                                                            disabled={brandsPage >= brandsTotalPages || loadingBrands}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                fetchBrands(brandsPage + 1);
                                                                            }}
                                                                            className="p-0 px-1 border-0 shadow-none text-dark bg-transparent"
                                                                            title="Next Page"
                                                                        >
                                                                            <FeatherIconCom iconName="ChevronRight" size={14} />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {loadingBrands || loadingBridgeData ? (
                                                                <div className="py-3 text-center small text-muted">
                                                                    <Spinner size="sm" color="primary" className="me-2" /> Loading Brands...
                                                                </div>
                                                            ) : brandsList.length > 0 ? (
                                                                <div
                                                                    className="d-flex flex-nowrap overflow-auto gap-2 py-2 px-2 border rounded bg-light"
                                                                    style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
                                                                >
                                                                    {brandsList.map((bItem: any, bIdx: number) => {
                                                                        const bId = bItem.BrandId ?? bItem.id ?? bItem.BrandCode ?? bIdx;
                                                                        const bName = bItem.BrandName || bItem.brand_name || bItem.name || `Brand #${bIdx + 1}`;
                                                                        const bCode = bItem.BrandCode || bItem.code;
                                                                        const bOrg = bItem.Orgunitid || bItem.org_unit_id;
                                                                        const isSelected = (selectedBrands[idx] || []).includes(bId);

                                                                        return (
                                                                            <div
                                                                                key={bIdx}
                                                                                onClick={() => toggleBrandSelection(idx, bId)}
                                                                                className={`p-2 rounded shadow-sm border ${isSelected ? 'border-primary' : 'border-secondary-subtle'}`}
                                                                                style={{
                                                                                    minWidth: '170px',
                                                                                    maxWidth: '210px',
                                                                                    flex: '0 0 auto',
                                                                                    cursor: 'pointer',
                                                                                    userSelect: 'none',
                                                                                    transition: 'all 0.2s ease',
                                                                                    borderWidth: isSelected ? '2px' : '1px',
                                                                                    backgroundColor: isSelected ? '#eef6ff' : '#ffffff'
                                                                                }}
                                                                            >
                                                                                <div className="d-flex align-items-center justify-content-between mb-1">
                                                                                    <span className="fw-bold text-dark text-truncate small me-2" title={bName}>{bName}</span>
                                                                                    <Input
                                                                                        type="checkbox"
                                                                                        checked={isSelected}
                                                                                        onChange={() => {}}
                                                                                        className="cursor-pointer"
                                                                                        style={{ pointerEvents: 'none' }}
                                                                                    />
                                                                                </div>
                                                                                {bCode && <div className="small text-muted" style={{ fontSize: '0.75rem' }}>Code: {bCode}</div>}
                                                                                {bOrg && <div className="small text-muted" style={{ fontSize: '0.75rem' }}>Org Unit: {bOrg}</div>}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <div className="p-3 text-muted border rounded bg-light small">No brands available from /api/getBrands</div>
                                                            )}
                                                        </div> */}

                                                        {/* Step Tabs Navigation Bar (3 Steps) */}
                                                        <Nav pills className="nav-justified gap-2 mb-4 p-1 bg-light rounded border flex-nowrap overflow-auto" style={{ scrollbarWidth: 'none' }}>
                                                            <NavItem>
                                                                <NavLink
                                                                    className={`cursor-pointer py-2 px-3 fw-bold small rounded d-flex align-items-center justify-content-center gap-1 ${getActiveStep(idx) === 1 ? 'active bg-primary text-white shadow-sm' : 'text-dark bg-white border'}`}
                                                                    onClick={() => setStep(idx, 1)}
                                                                    style={{ cursor: 'pointer', transition: 'all 0.2s ease', whiteSpace: 'nowrap' }}
                                                                >
                                                                    <FeatherIconCom iconName="Award" size={15} />
                                                                    <span>Step 01: Select Detailing Brands</span>
                                                                    {(selectedDetailingBrands[idx] || []).length > 0 && (
                                                                        <Badge color="light" pill className="ms-1 text-primary fw-bold">
                                                                            {(selectedDetailingBrands[idx] || []).length}
                                                                        </Badge>
                                                                    )}
                                                                </NavLink>
                                                            </NavItem>
                                                            <NavItem>
                                                                <NavLink
                                                                    className={`cursor-pointer py-2 px-3 fw-bold small rounded d-flex align-items-center justify-content-center gap-1 ${getActiveStep(idx) === 2 ? 'active bg-success text-white shadow-sm' : 'text-dark bg-white border'}`}
                                                                    onClick={() => setStep(idx, 2)}
                                                                    style={{ cursor: 'pointer', transition: 'all 0.2s ease', whiteSpace: 'nowrap' }}
                                                                >
                                                                    <FeatherIconCom iconName="Box" size={15} />
                                                                    <span>Step 02: SGPI Out & Balances</span>
                                                                    {Object.keys(selectedSgpiQuantities[idx] || {}).length > 0 && (
                                                                        <Badge color="light" pill className="ms-1 text-success fw-bold">
                                                                            {Object.keys(selectedSgpiQuantities[idx] || {}).length}
                                                                        </Badge>
                                                                    )}
                                                                </NavLink>
                                                            </NavItem>
                                                            <NavItem>
                                                                <NavLink
                                                                    className={`cursor-pointer py-2 px-3 fw-bold small rounded d-flex align-items-center justify-content-center gap-1 ${getActiveStep(idx) === 3 ? 'active bg-info text-white shadow-sm' : 'text-dark bg-white border'}`}
                                                                    onClick={() => setStep(idx, 3)}
                                                                    style={{ cursor: 'pointer', transition: 'all 0.2s ease', whiteSpace: 'nowrap' }}
                                                                >
                                                                    <FeatherIconCom iconName="Users" size={15} />
                                                                    <span>Step 03: Manager & DCR Feedback</span>
                                                                    {((selectedReportingManagers[idx] || []).length > 0 || (ncaComments && ncaComments.trim())) && (
                                                                        <Badge color="light" pill className="ms-1 text-info fw-bold">
                                                                            {(selectedReportingManagers[idx] || []).length > 0 ? (selectedReportingManagers[idx] || []).length : '✓'}
                                                                        </Badge>
                                                                    )}
                                                                </NavLink>
                                                            </NavItem>
                                                        </Nav>

                                                        {/* Step 01: Detailing Brands Selection */}
                                                        {getActiveStep(idx) === 1 && (
                                                            <div className="mb-4">
                                                                <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                                                                    <label className="form-label small fw-bold text-dark mb-0" style={{ color: '#000000' }}>
                                                                        <FeatherIconCom iconName="Award" size={14} className="me-1 text-primary" />
                                                                        Step 01: Select Detailing Brands
                                                                    </label>
                                                                    <Badge color="primary" pill className="px-2 py-1 text-white">
                                                                        {(selectedDetailingBrands[idx] || []).length} Selected
                                                                    </Badge>
                                                                </div>
                                                                {loadingBridgeData || loadingBrands || loadingEd ? (
                                                                    <div className="py-3 text-center small text-muted">
                                                                        <Spinner size="sm" color="primary" className="me-2" /> Loading Detailing Brands...
                                                                    </div>
                                                                ) : detailingBrandsList.length > 0 ? (
                                                                    <div
                                                                        className="d-flex flex-nowrap overflow-auto gap-2 py-2 px-2 border rounded bg-light"
                                                                        style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
                                                                    >
                                                                        {detailingBrandsList.map((bItem: any, bIdx: number) => {
                                                                            const bId = bItem.BrandId ?? bItem.id ?? bItem.BrandCode ?? bIdx;
                                                                            const bName = bItem.BrandName || bItem.brand_name || bItem.name || `Detailing Brand #${bIdx + 1}`;
                                                                            const bCode = bItem.BrandCode || bItem.code;
                                                                            const bOrg = bItem.Orgunitid || bItem.org_unit_id;
                                                                            const isSelected = (selectedDetailingBrands[idx] || []).includes(bId);

                                                                            return (
                                                                                <div
                                                                                    key={bIdx}
                                                                                    onClick={() => toggleDetailingBrandSelection(idx, bId)}
                                                                                    className={`p-2 rounded shadow-sm border ${isSelected ? 'border-primary' : 'border-secondary-subtle'}`}
                                                                                    style={{
                                                                                        minWidth: '170px',
                                                                                        maxWidth: '210px',
                                                                                        flex: '0 0 auto',
                                                                                        cursor: 'pointer',
                                                                                        userSelect: 'none',
                                                                                        transition: 'all 0.2s ease',
                                                                                        borderWidth: isSelected ? '2px' : '1px',
                                                                                        backgroundColor: isSelected ? '#eff6ff' : '#ffffff'
                                                                                    }}
                                                                                >
                                                                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                                                                        <span className="fw-bold text-dark text-truncate small me-2" title={bName}>{bName}</span>
                                                                                        <Input
                                                                                            type="checkbox"
                                                                                            checked={isSelected}
                                                                                            onChange={() => { }}
                                                                                            className="cursor-pointer"
                                                                                            style={{ pointerEvents: 'none' }}
                                                                                        />
                                                                                    </div>
                                                                                    {bCode && <div className="small text-muted" style={{ fontSize: '0.75rem' }}>Code: {bCode}</div>}
                                                                                    {bOrg && <div className="small text-muted" style={{ fontSize: '0.75rem' }}>Org Unit: {bOrg}</div>}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <div className="p-3 text-muted border rounded bg-light small">
                                                                        No detailing brands match the BrandId from Educational Presentations.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Step 02: SGPI Balance Selection */}
                                                        {getActiveStep(idx) === 2 && (
                                                            <div className="mb-4">
                                                                <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                                                                    <label className="form-label small fw-bold text-dark mb-0" style={{ color: '#000000' }}>
                                                                        <FeatherIconCom iconName="Box" size={14} className="me-1 text-success" />
                                                                        Step 02: Select SGPI
                                                                    </label>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <Badge color="success" pill className="px-2 py-1">
                                                                            {(selectedSgpiBalances[idx] || []).length} Selected
                                                                        </Badge>
                                                                        <div className="d-flex align-items-center gap-1 border rounded px-2 py-1 bg-white shadow-sm">
                                                                            <span className="small text-muted me-1 fw-semibold" style={{ fontSize: '0.78rem' }}>
                                                                                Page {sgpiPage} of {sgpiTotalPages}
                                                                            </span>
                                                                            <Button
                                                                                color="light"
                                                                                size="sm"
                                                                                disabled={sgpiPage <= 1 || loadingSgpi}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    fetchSgpiBalance(sgpiPage - 1);
                                                                                }}
                                                                                className="p-0 px-1 border-0 shadow-none text-dark bg-transparent"
                                                                                title="Previous Page"
                                                                            >
                                                                                <FeatherIconCom iconName="ChevronLeft" size={14} />
                                                                            </Button>
                                                                            <Button
                                                                                color="light"
                                                                                size="sm"
                                                                                disabled={sgpiPage >= sgpiTotalPages || loadingSgpi}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    fetchSgpiBalance(sgpiPage + 1);
                                                                                }}
                                                                                className="p-0 px-1 border-0 shadow-none text-dark bg-transparent"
                                                                                title="Next Page"
                                                                            >
                                                                                <FeatherIconCom iconName="ChevronRight" size={14} />
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {loadingSgpi || loadingBridgeData ? (
                                                                    <div className="py-3 text-center small text-muted">
                                                                        <Spinner size="sm" color="success" className="me-2" /> Loading SGPI Balance...
                                                                    </div>
                                                                ) : sgpiBalanceList.length > 0 ? (
                                                                    <div className="border rounded p-3 bg-light">
                                                                        {/* Helper to render a category row */}
                                                                        {[
                                                                            { title: "Active Samples", items: sgpiCategorized.activeSamples, badgeColor: "success" },
                                                                            { title: "Active Gifts", items: sgpiCategorized.activeGifts, badgeColor: "info" },
                                                                            { title: "Active Promo", items: sgpiCategorized.activePromo, badgeColor: "primary" },
                                                                            { title: "Active Others", items: sgpiCategorized.activeOthers, badgeColor: "secondary" },
                                                                            { title: "Previous Samples", items: sgpiCategorized.previousSamples, badgeColor: "outline-success" },
                                                                            { title: "Previous Gifts", items: sgpiCategorized.previousGifts, badgeColor: "outline-info" },
                                                                            { title: "Previous Promo", items: sgpiCategorized.previousPromo, badgeColor: "outline-primary" },
                                                                            { title: "Previous Others", items: sgpiCategorized.previousOthers, badgeColor: "outline-secondary" },
                                                                        ].map((cat, cIdx) => {
                                                                            if (!cat.items || cat.items.length === 0) return null;
                                                                            return (
                                                                                <div key={cIdx} className="mb-3">
                                                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                                                        <span className="fw-bold text-dark small">{cat.title}</span>
                                                                                        <Badge color="dark" pill style={{ fontSize: '0.75rem' }}>
                                                                                            {cat.items.length}
                                                                                        </Badge>
                                                                                    </div>
                                                                                    <div
                                                                                        className="d-flex flex-nowrap overflow-auto gap-2 py-2 px-2 border rounded bg-white"
                                                                                        style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
                                                                                    >
                                                                                        {cat.items.map((sgpiItem: any, sIdx: number) => {
                                                                                            const sId = sgpiItem.SgpiId ?? sgpiItem.id ?? sgpiItem.code ?? sIdx;
                                                                                            const sName = sgpiItem.SgpiName || sgpiItem.ItemName || sgpiItem.BrandName || sgpiItem.brand_name || sgpiItem.name || `SGPI #${sIdx + 1}`;
                                                                                            const rawBal = sgpiItem.Balance ?? sgpiItem.balance ?? sgpiItem.stock ?? sgpiItem.quantity ?? sgpiItem.qty;
                                                                                            const maxBal = rawBal !== undefined && rawBal !== null && !isNaN(Number(rawBal)) ? Number(rawBal) : undefined;
                                                                                            const currentQty = (selectedSgpiQuantities[idx] || {})[sId] || 0;
                                                                                            const isSelected = currentQty > 0;

                                                                                            return (
                                                                                                <div
                                                                                                    key={sIdx}
                                                                                                    className={`p-2 rounded shadow-sm border ${isSelected ? 'border-success' : 'border-secondary-subtle'}`}
                                                                                                    style={{
                                                                                                        minWidth: '190px',
                                                                                                        maxWidth: '230px',
                                                                                                        flex: '0 0 auto',
                                                                                                        userSelect: 'none',
                                                                                                        transition: 'all 0.2s ease',
                                                                                                        borderWidth: isSelected ? '2px' : '1px',
                                                                                                        backgroundColor: isSelected ? '#f2faf5' : '#ffffff'
                                                                                                    }}
                                                                                                >
                                                                                                    <div className="fw-bold text-dark text-truncate small mb-1" title={sName}>{sName}</div>
                                                                                                    {rawBal !== undefined && (
                                                                                                        <div className="small fw-semibold text-muted" style={{ fontSize: '0.8rem' }}>
                                                                                                            Max Balance: <span className="text-dark fw-bold">{rawBal}</span>
                                                                                                        </div>
                                                                                                    )}
                                                                                                    {(sgpiItem.OutletTypeId || sgpiItem.outlettype_id) && (
                                                                                                        <div className="small text-muted" style={{ fontSize: '0.75rem' }}>
                                                                                                            Outlet Type: {sgpiItem.OutletTypeId || sgpiItem.outlettype_id}
                                                                                                        </div>
                                                                                                    )}
                                                                                                    <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top">
                                                                                                        <span className="small text-muted fw-bold">Qty:</span>
                                                                                                        <div className="d-flex align-items-center gap-1">                                                                                                         <Button
                                                                                                            color="light"
                                                                                                            size="sm"
                                                                                                            disabled={currentQty <= 0}
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                updateSgpiQuantity(idx, sId, currentQty - 1, maxBal);
                                                                                                            }}
                                                                                                            className="p-0 border text-dark bg-white shadow-none d-flex align-items-center justify-content-center"
                                                                                                            style={{ width: '26px', height: '26px', cursor: 'pointer' }}
                                                                                                            title="Decrease Quantity"
                                                                                                        >
                                                                                                            <FeatherIconCom iconName="Minus" size={12} />
                                                                                                        </Button>
                                                                                                            <span
                                                                                                                className={`fw-bold px-2 small ${currentQty > 0 ? 'text-success' : 'text-dark'}`}
                                                                                                                style={{ minWidth: '24px', textAlign: 'center' }}
                                                                                                            >
                                                                                                                {currentQty}
                                                                                                            </span>
                                                                                                            <Button
                                                                                                                color="light"
                                                                                                                size="sm"
                                                                                                                disabled={maxBal !== undefined && currentQty >= maxBal}
                                                                                                                onClick={(e) => {
                                                                                                                    e.stopPropagation();
                                                                                                                    updateSgpiQuantity(idx, sId, currentQty + 1, maxBal);
                                                                                                                }}
                                                                                                                className="p-0 border text-dark bg-white shadow-none d-flex align-items-center justify-content-center"
                                                                                                                style={{ width: '26px', height: '26px', cursor: 'pointer' }}
                                                                                                                title="Increase Quantity"
                                                                                                            >
                                                                                                                <FeatherIconCom iconName="Plus" size={12} />
                                                                                                            </Button>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <div className="p-3 text-muted border rounded bg-light small">No SGPI balance records available from /api/getSgpiBalance</div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Step 03: Manager working with you & DCR Feedback */}
                                                        {getActiveStep(idx) === 3 && (
                                                            <div>
                                                                {/* 1. Manager Selection */}
                                                                <div className="mb-4">
                                                                    <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                                                                        <label className="form-label small fw-bold text-dark mb-0" style={{ color: '#000000' }}>
                                                                            <FeatherIconCom iconName="Users" size={14} className="me-1 text-primary" />
                                                                            Manager working with you?
                                                                        </label>
                                                                        <Badge color="primary" pill className="px-2 py-1">
                                                                            {(selectedReportingManagers[idx] || []).length} Selected
                                                                        </Badge>
                                                                    </div>
                                                                    {loadingReporting ? (
                                                                        <div className="py-3 text-center small text-muted">
                                                                            <Spinner size="sm" color="primary" className="me-2" /> Loading Reporting Managers...
                                                                        </div>
                                                                    ) : profileReportingList.length > 0 ? (
                                                                        <div
                                                                            className="d-flex flex-nowrap overflow-auto gap-2 py-2 px-2 border rounded bg-light"
                                                                            style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
                                                                        >
                                                                            {profileReportingList.map((mgrItem: any, mIdx: number) => {
                                                                                const mId = mgrItem.EmployeeId ?? mgrItem.employee_id ?? mgrItem.Employeeid ?? mgrItem.id ?? mgrItem.user_id ?? mIdx;
                                                                                const rawFn = mgrItem.FirstName || mgrItem.first_name || mgrItem.name || '';
                                                                                const rawLn = (mgrItem.LastName && mgrItem.LastName !== '.') ? mgrItem.LastName : '';
                                                                                const mName = (rawFn || rawLn) ? `${rawFn} ${rawLn}`.trim() : (mgrItem.employee_name || mgrItem.user_name || `Manager #${mIdx + 1}`);
                                                                                const mDesig = (typeof mgrItem.Designations === 'object' ? mgrItem.Designations?.Designation : null) || mgrItem.designation || mgrItem.role || mgrItem.Designation || '';
                                                                                const isSelected = (selectedReportingManagers[idx] || []).includes(mId);

                                                                                return (
                                                                                    <div
                                                                                        key={mIdx}
                                                                                        onClick={() => toggleReportingManagerSelection(idx, mId)}
                                                                                        className={`p-2 rounded shadow-sm border ${isSelected ? 'border-primary' : 'border-secondary-subtle'}`}
                                                                                        style={{
                                                                                            minWidth: '180px',
                                                                                            maxWidth: '220px',
                                                                                            flex: '0 0 auto',
                                                                                            cursor: 'pointer',
                                                                                            userSelect: 'none',
                                                                                            transition: 'all 0.2s ease',
                                                                                            borderWidth: isSelected ? '2px' : '1px',
                                                                                            backgroundColor: isSelected ? '#f0f4ff' : '#ffffff'
                                                                                        }}
                                                                                    >
                                                                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                                                                            <span className="fw-bold text-dark text-truncate small me-2" title={mName}>{mName}</span>
                                                                                            <Input
                                                                                                type="checkbox"
                                                                                                checked={isSelected}
                                                                                                onChange={() => { }}
                                                                                                className="cursor-pointer"
                                                                                                style={{ pointerEvents: 'none' }}
                                                                                            />
                                                                                        </div>
                                                                                        {mDesig && <div className="small text-muted text-truncate" style={{ fontSize: '0.75rem' }}>{mDesig}</div>}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="p-3 text-muted border rounded bg-light small">No reporting managers available</div>
                                                                    )}
                                                                </div>

                                                                {/* 2. DCR Feedback / Remark */}
                                                                <div className="mb-4">
                                                                    <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>
                                                                        <FeatherIconCom iconName="MessageSquare" size={14} className="me-1 text-info" />
                                                                        DCR Feedback / Remarks
                                                                    </label>
                                                                    <Input
                                                                        type="textarea"
                                                                        rows={3}
                                                                        placeholder="Enter comments / feedback..."
                                                                        value={ncaComments}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            setFwList(prev => prev.map((it, i) => i === idx ? { ...it, nca_comments: val } : it));
                                                                        }}
                                                                        className="bg-white border text-dark shadow-none py-2 px-3 fw-semibold"
                                                                        style={{ fontSize: '0.9rem', color: '#000000' }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Wizard Step Navigation Footer & Submit */}
                                                        <div className="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
                                                            <div>
                                                                {getActiveStep(idx) > 1 && (
                                                                    <Button
                                                                        color="light"
                                                                        className="px-3 py-2 fw-semibold border d-flex align-items-center gap-1 shadow-sm text-dark bg-white"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setStep(idx, getActiveStep(idx) - 1);
                                                                        }}
                                                                    >
                                                                        <FeatherIconCom iconName="ArrowLeft" size={16} />
                                                                        <span>Previous Step</span>
                                                                    </Button>
                                                                )}
                                                            </div>

                                                            <div className="d-flex align-items-center gap-2">
                                                                {getActiveStep(idx) < 3 ? (
                                                                    <Button
                                                                        color="primary"
                                                                        className="px-4 py-2 fw-bold text-white d-flex align-items-center gap-1 shadow-sm border-0"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setStep(idx, getActiveStep(idx) + 1);
                                                                        }}
                                                                    >
                                                                        <span>Next Step</span>
                                                                        <FeatherIconCom iconName="ArrowRight" size={16} />
                                                                    </Button>
                                                                ) : (
                                                                    !isReported && (
                                                                        <Button
                                                                            color="success"
                                                                            className="px-4 py-2 fw-bold border-0 text-white d-flex align-items-center gap-2 shadow-sm"
                                                                            disabled={submittingFwIndex === idx}
                                                                            style={{ backgroundColor: '#198754', fontSize: '0.9rem', borderRadius: '8px', cursor: 'pointer' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleUpdateFieldWork(idx);
                                                                            }}
                                                                        >
                                                                            {submittingFwIndex === idx ? <Spinner size="sm" color="light" /> : (
                                                                                <>
                                                                                    <FeatherIconCom iconName="CheckCircle" size={16} />
                                                                                    <span>Submit DCR Record</span>
                                                                                </>
                                                                            )}
                                                                        </Button>
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                    </ModalBody>
                                                </Modal>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        ) : (
                            <div className="text-center py-5" style={{ color: '#000000' }}>
                                <FeatherIconCom iconName="Briefcase" size={48} className="mb-3 text-dark" />
                                <h5 className="fw-bold text-dark" style={{ color: '#000000' }}>No Field Work Records Found</h5>
                                <p className="small mb-0 text-dark fw-semibold" style={{ color: '#000000' }}>
                                    No field work data is available for {moment(selectedDate).format('DD MMM YYYY')}.
                                </p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>

            {/* Unplanned Call Modal */}
            <Modal isOpen={unplannedModalOpen} toggle={() => setUnplannedModalOpen(!unplannedModalOpen)} size="lg" centered>
                <ModalHeader toggle={() => setUnplannedModalOpen(!unplannedModalOpen)} className="bg-light border-bottom">
                    <FeatherIconCom iconName="PlusCircle" size={18} className="me-2 text-primary" />
                    <span className="fw-bold text-dark">Unplanned Call +</span>
                </ModalHeader>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    {/* Search & Filter & Pagination Bar */}
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: '280px' }}>
                            <Input
                                type="text"
                                placeholder="Search Outlet Name / Code..."
                                value={outletSearchText}
                                onChange={(e) => setOutletSearchText(e.target.value)}
                                className="bg-white border text-dark shadow-none fw-semibold py-2 px-3 flex-grow-1"
                                style={{ fontSize: '0.88rem' }}
                            />
                            <Button
                                color="primary"
                                outline
                                className="d-flex align-items-center gap-1 py-2 px-3 fw-bold border bg-white"
                                style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                                onClick={() => {
                                    setFilterModalOpen(true);
                                    fetchFilterData();
                                }}
                            >
                                <FeatherIconCom iconName="Filter" size={15} />
                                <span>Filter</span>
                                {(selectedOutletTypeIds.length > 0 || selectedBeatIds.length > 0 || selectedTerritoryIds.length > 0) && (
                                    <Badge color="primary" pill className="ms-1">
                                        {selectedOutletTypeIds.length + selectedBeatIds.length + selectedTerritoryIds.length}
                                    </Badge>
                                )}
                            </Button>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="small text-muted fw-bold">
                                Page {outletsPage} of {outletsTotalPages}
                            </span>
                            <Button
                                color="light"
                                size="sm"
                                disabled={outletsPage <= 1 || loadingOutlets}
                                onClick={() => fetchOutletsList(outletsPage - 1)}
                                className="p-1 border text-dark bg-white shadow-none"
                                title="Previous Page"
                            >
                                <FeatherIconCom iconName="ChevronLeft" size={16} />
                            </Button>
                            <Button
                                color="light"
                                size="sm"
                                disabled={outletsPage >= outletsTotalPages || loadingOutlets}
                                onClick={() => fetchOutletsList(outletsPage + 1)}
                                className="p-1 border text-dark bg-white shadow-none"
                                title="Next Page"
                            >
                                <FeatherIconCom iconName="ChevronRight" size={16} />
                            </Button>
                        </div>
                    </div>

                    {/* Outlets Table */}
                    {loadingOutlets ? (
                        <div className="py-5 text-center text-muted">
                            <Spinner color="primary" size="sm" className="me-2" />
                            <span>Fetching Outlets...</span>
                        </div>
                    ) : filteredOutlets.length > 0 ? (
                        <div className="table-responsive border rounded" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                            <Table hover responsive className="mb-0 align-middle text-nowrap">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th style={{ width: '40px' }}>
                                            <Input
                                                type="checkbox"
                                                checked={filteredOutlets.length > 0 && selectedOutletIds.length === filteredOutlets.length}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        const allIds = filteredOutlets.map(o => Number(o.OutletOrgId || o.OutletOrgDataId || o.outlet_org_data_id || o.id || o.Id));
                                                        setSelectedOutletIds(allIds);
                                                    } else {
                                                        setSelectedOutletIds([]);
                                                    }
                                                }}
                                            />
                                        </th>
                                        <th>Outlet Name</th>
                                        <th>Org Code</th>
                                        <th>Beat Name</th>
                                        <th>Last Visit Date</th>
                                        <th>Classification</th>
                                        <th>Visit Fq / Visited</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOutlets.map((outletItem: any, oIdx: number) => {
                                        const oId = Number(outletItem.OutletOrgId || outletItem.OutletOrgDataId || outletItem.outlet_org_data_id || outletItem.id || outletItem.Id || oIdx);
                                        const oName = outletItem.OutletName || outletItem.outlet_name || 'N/A';
                                        const oOrgCode = outletItem.OutletOrgCode || outletItem.OutletCode || outletItem.PCode || outletItem.p_code || 'N/A';
                                        const beatName = outletItem.BeatName || outletItem.beat_name || 'N/A';

                                        const rawLastVisit = outletItem.LastVisitDate || outletItem.last_visit_date;
                                        const lastVisitFormatted = rawLastVisit && moment(rawLastVisit).isValid()
                                            ? moment(rawLastVisit).format('DD-MM-YY')
                                            : 'N/A';

                                        const classification = outletItem.Classification || outletItem.OutlettypeName || outletItem.outlet_type_name || 'N/A';
                                        const visitFq = outletItem.VisitFq ?? outletItem.visit_fq ?? 0;
                                        const visitedCount = outletItem.VisitedCount ?? outletItem.visited_count ?? 0;
                                        const fqDisplay = `${visitFq} / ${visitedCount}`;

                                        const isChecked = selectedOutletIds.includes(oId);

                                        return (
                                            <tr
                                                key={oIdx}
                                                onClick={() => {
                                                    setSelectedOutletIds(prev =>
                                                        prev.includes(oId) ? prev.filter(id => id !== oId) : [...prev, oId]
                                                    );
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td>
                                                    <Input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => { }}
                                                        style={{ pointerEvents: 'none' }}
                                                    />
                                                </td>
                                                <td className="fw-semibold text-dark">{oName}</td>
                                                <td><code>{oOrgCode}</code></td>
                                                <td>{beatName}</td>
                                                <td>{lastVisitFormatted}</td>
                                                <td><Badge color="light" className="text-dark border">{classification}</Badge></td>
                                                <td className="fw-bold text-success">{fqDisplay}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted border rounded bg-light">
                            <FeatherIconCom iconName="Info" size={24} className="mb-2 text-secondary" />
                            <div>No outlets found.</div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="bg-light d-flex justify-content-between">
                    <span className="small fw-semibold text-muted">
                        {selectedOutletIds.length} Outlet(s) Selected
                    </span>
                    <div className="d-flex gap-2">
                        <Button color="secondary" size="sm" outline onClick={() => setUnplannedModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            size="sm"
                            className="px-4 fw-bold text-white"
                            disabled={selectedOutletIds.length === 0 || submittingUnplannedCall}
                            onClick={handleSubmitUnplannedCall}
                        >
                            {submittingUnplannedCall ? (
                                <>
                                    <Spinner size="sm" color="light" className="me-1" /> Submitting...
                                </>
                            ) : (
                                <>
                                    <FeatherIconCom iconName="Check" size={14} className="me-1" /> Submit Unplanned Call
                                </>
                            )}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>

            {/* Outlets Filter Modal */}
            <Modal isOpen={filterModalOpen} toggle={() => setFilterModalOpen(false)} size="lg" centered>
                <ModalHeader toggle={() => setFilterModalOpen(false)} className="border-bottom py-3">
                    <div className="d-flex align-items-center gap-2">
                        <FeatherIconCom iconName="Filter" size={18} className="text-primary" />
                        <span className="fw-bold text-dark fs-5">Filters</span>
                    </div>
                </ModalHeader>
                <ModalBody className="p-0">
                    <Row className="g-0" style={{ minHeight: '340px' }}>
                        {/* Left Column: Navigation Tabs */}
                        <Col xs={4} sm={3} className="border-end bg-light p-0">
                            <div className="nav flex-column me-0" role="tablist">
                                <button
                                    className={`nav-link text-start rounded-0 py-3 px-3 fw-bold border-bottom ${activeFilterTab === 'type' ? 'active bg-white text-primary border-start border-4 border-primary' : 'text-dark'}`}
                                    onClick={() => setActiveFilterTab('type')}
                                    type="button"
                                    style={{ fontSize: '0.9rem' }}
                                >
                                    Type
                                </button>
                                <button
                                    className={`nav-link text-start rounded-0 py-3 px-3 fw-bold border-bottom ${activeFilterTab === 'beats' ? 'active bg-white text-primary border-start border-4 border-primary' : 'text-dark'}`}
                                    onClick={() => setActiveFilterTab('beats')}
                                    type="button"
                                    style={{ fontSize: '0.9rem' }}
                                >
                                    Patch / Beats
                                </button>
                                <button
                                    className={`nav-link text-start rounded-0 py-3 px-3 fw-bold border-bottom ${activeFilterTab === 'territories' ? 'active bg-white text-primary border-start border-4 border-primary' : 'text-dark'}`}
                                    onClick={() => setActiveFilterTab('territories')}
                                    type="button"
                                    style={{ fontSize: '0.9rem' }}
                                >
                                    Territory
                                </button>
                            </div>
                        </Col>

                        {/* Right Column: Pill Buttons */}
                        <Col xs={8} sm={9} className="p-4 bg-white">
                            {loadingFilterData ? (
                                <div className="py-5 text-center text-muted">
                                    <Spinner color="primary" size="sm" className="me-2" />
                                    <span>Loading Filter Options...</span>
                                </div>
                            ) : (
                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                    {activeFilterTab === 'type' && (
                                        filterOutletTypes.length > 0 ? (
                                            filterOutletTypes.map((item: any, idx: number) => {
                                                const typeId = item.OutlettypeId ?? item.id ?? item.OutletTypeId;
                                                const typeName = item.OutlettypeName || item.outlet_type_name || item.TypeName || item.name || item.OutletType || (typeof item === 'string' ? item : `Type #${idx}`);
                                                const isSelected = selectedOutletTypeIds.includes(typeId) || selectedOutletTypeIds.includes(String(typeId)) || selectedOutletTypeIds.includes(Number(typeId));
                                                return (
                                                    <Button
                                                        key={idx}
                                                        color={isSelected ? "primary" : "light"}
                                                        size="sm"
                                                        className={`rounded-pill px-3 py-2 fw-bold border ${isSelected ? 'text-white shadow-sm' : 'text-dark bg-light border-secondary-subtle'}`}
                                                        style={{ fontSize: '0.82rem', textTransform: 'uppercase' }}
                                                        onClick={() => toggleOutletTypeSelection(typeId)}
                                                    >
                                                        {typeName}
                                                    </Button>
                                                );
                                            })
                                        ) : (
                                            <span className="text-muted small">No Outlet Types found</span>
                                        )
                                    )}

                                    {activeFilterTab === 'beats' && (
                                        filterBeats.length > 0 ? (
                                            filterBeats.map((item: any, idx: number) => {
                                                const beatId = item.BeatId ?? item.id ?? item.beat_id;
                                                const beatName = item.BeatName || item.beat_name || item.name || item.BeatCode || (typeof item === 'string' ? item : `Beat #${idx}`);
                                                const isSelected = selectedBeatIds.includes(beatId) || selectedBeatIds.includes(String(beatId)) || selectedBeatIds.includes(Number(beatId));
                                                return (
                                                    <Button
                                                        key={idx}
                                                        color={isSelected ? "primary" : "light"}
                                                        size="sm"
                                                        className={`rounded-pill px-3 py-2 fw-bold border ${isSelected ? 'text-white shadow-sm' : 'text-dark bg-light border-secondary-subtle'}`}
                                                        style={{ fontSize: '0.82rem' }}
                                                        onClick={() => toggleBeatSelection(beatId)}
                                                    >
                                                        {beatName}
                                                    </Button>
                                                );
                                            })
                                        ) : (
                                            <span className="text-muted small">No Beats / Patches found</span>
                                        )
                                    )}

                                    {activeFilterTab === 'territories' && (
                                        filterTerritories.length > 0 ? (
                                            filterTerritories.map((item: any, idx: number) => {
                                                const terrId = item.TerritoryId ?? item.territory_id ?? item.id ?? idx;
                                                const terrName = item.TerritoryName || item.territory_name || item.name || `Territory #${terrId}`;
                                                const isSelected = selectedTerritoryIds.includes(terrId) || selectedTerritoryIds.includes(String(terrId));
                                                return (
                                                    <Button
                                                        key={idx}
                                                        color={isSelected ? "primary" : "light"}
                                                        size="sm"
                                                        className={`rounded-pill px-3 py-2 fw-bold border ${isSelected ? 'text-white shadow-sm' : 'text-dark bg-light border-secondary-subtle'}`}
                                                        style={{ fontSize: '0.82rem' }}
                                                        onClick={() => toggleTerritorySelection(terrId)}
                                                    >
                                                        {terrName}
                                                    </Button>
                                                );
                                            })
                                        ) : (
                                            <span className="text-muted small">No Territories found</span>
                                        )
                                    )}
                                </div>
                            )}
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter className="bg-light border-top d-flex justify-content-between">
                    <Button
                        color="link"
                        className="text-decoration-none text-muted fw-bold p-0"
                        onClick={handleClearFilter}
                    >
                        Clear
                    </Button>
                    <Button
                        color="primary"
                        className="px-4 fw-bold text-white"
                        onClick={handleApplyFilter}
                    >
                        Apply Filter
                    </Button>
                </ModalFooter>
            </Modal>
            {/* Geofencing Validation Modal */}
            <Modal isOpen={geoModalOpen} toggle={() => setGeoModalOpen(false)} centered size="md">
                <ModalHeader toggle={() => setGeoModalOpen(false)} className={geoModalConfig.type === 'gps_not_found' ? 'bg-warning text-dark' : 'bg-danger text-white'}>
                    <FeatherIconCom iconName="MapPin" size={18} className="me-2" />
                    {geoModalConfig.title}
                </ModalHeader>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    <p className="mb-0 text-dark" style={{ fontSize: '0.95rem' }}>
                        {geoModalConfig.message}
                    </p>
                </ModalBody>
                <ModalFooter className="bg-light">
                    <Button color="secondary" outline size="sm" onClick={() => setGeoModalOpen(false)} disabled={verifyingLocation}>
                        Close
                    </Button>
                    {(geoModalConfig.type === 'gps_not_found' || geoModalConfig.type === 'override_available') && (
                        <Button
                            color="primary"
                            size="sm"
                            className="text-white px-4 fw-semibold"
                            onClick={handleConfirmGpsOverride}
                            disabled={verifyingLocation}
                        >
                            {verifyingLocation ? <Spinner size="sm" color="light" /> : (geoModalConfig.type === 'override_available' ? 'Request Geo Update' : 'Confirm & Proceed')}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </Fragment>
    );
}
